import { useContext, useEffect, useRef, useState } from 'react';
import { BrandSystemContext } from '../contexts/BrandSystemContext';
import { PayFitColor } from '../types/module';

export type TCorner = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
// if you want to change the color, pass it as before:border-[*color*] of after:border-[*color*] in second parameter
export const addDivCorner = (corner: TCorner, color?: string) => {
  switch (corner) {
    case 'top-left':
      return `before:block before:content-[""] before:w-[20px] before:h-[20px] before:absolute before:top-[-10px] before:left-[-10px] before:border-tl-[30px] before:border-t-[2px] before:border-l-[2px] before:rounded-tl-[14px] ${
        color || 'before:border-[#dfe6ed]'
      }`;
    case `top-right`:
      return `after:block after:content-[""] after:w-[20px] after:h-[20px] after:absolute after:top-[-10px] after:right-[-10px]  after:border-tr-[30px] after:border-t-[2px] after:border-r-[2px] after:rounded-tr-[14px] ${
        color || 'after:border-[#dfe6ed]'
      }`;
    case `bottom-left`:
      return `before:block before:content-[""] before:w-[20px] before:h-[20px] before:absolute before:bottom-[-10px] before:left-[-10px] before:border-bl-[30px] before:border-b-[2px] before:border-l-[2px] before:rounded-bl-[14px] ${
        color || 'before:border-[#dfe6ed]'
      }`;
    case `bottom-right`:
      return `after:block after:content-[""] after:w-[20px] after:h-[20px] after:absolute after:bottom-[-10px] after:right-[-10px] after:border-br-[30px] after:border-b-[2px] after:border-r-[2px] after:rounded-br-[14px] ${
        color || 'after:border-[#dfe6ed]'
      }`;
    default:
      return ``;
  }
};

export const getBgColor = (
  color?: PayFitColor,
  codeColor: CodeColorProps = 21,
  classDefault = '',
): string => {
  switch (color) {
    case 'neutral':
      return `bg-neutral-${codeColor}`;
    case 'mint':
      return `bg-mint-${codeColor}`;
    case 'purple':
      return `bg-purple-${codeColor}`;
    case 'coral':
      return `bg-coral-${codeColor}`;
    case 'azur':
      return `bg-azur-${codeColor}`;
    case 'yellow':
      return `bg-yellow-${codeColor}`;
    case 'white':
      return `bg-neutral-1`;
    case 'blue':
      return `bg-blue-${codeColor}`;
    case 'grey':
      return `bg-[#F9FAFB]`;
    default:
      return classDefault;
  }
};

export type CodeColorProps = 21 | 51 | 101 | 301;
export const applyBgColor = (
  color?: PayFitColor,
  isDark = false,
  classDefault = 'bg-neutral-01',
  forceCodeColor?: CodeColorProps,
) => {
  let _codeColor: CodeColorProps = isDark ? 301 : 21;

  _codeColor = forceCodeColor ? forceCodeColor : _codeColor;

  return getBgColor(color, _codeColor, classDefault);
};

export const applyBgLightColor = (color?: PayFitColor): string => {
  return applyBgColor(color, false);
};

export const applyBgDarkColor = (color?: PayFitColor) => {
  return applyBgColor(color, true);
};

export type TScreenSize = 'xl' | 'lg' | 'md' | 'sm' | 'xs' | '';

export function useScreenSize(): TScreenSize {
  const [screen, setScreen] = useState<TScreenSize>('');

  useEffect(() => {
    if (typeof window === 'undefined') return;

    function handleResize() {
      if (window?.innerWidth < 480) {
        setScreen('xs');
      } else if (window?.innerWidth < 768) {
        setScreen('sm');
      } else if (window?.innerWidth < 1024) {
        setScreen('md');
      } else if (window?.innerWidth < 1280) {
        setScreen('lg');
      } else {
        setScreen('xl');
      }
    }

    window?.addEventListener('resize', handleResize);

    handleResize();

    return () => window?.removeEventListener('resize', handleResize);
  }, []);

  return screen;
}

export const usePreviousValue = (value: any) => {
  const ref = useRef();

  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
};

export const useWindowResize = (handler: any) => {
  const refHandler = useRef<any>();

  useEffect(() => {
    // Avoid infinite re-render
    refHandler.current = handler;
  }, [handler]);

  useEffect(() => {
    const eventHandler = (e: any) => refHandler.current(e);

    window.addEventListener('resize', eventHandler);

    return () => window.removeEventListener('resize', eventHandler);
  }, []);
};

export const useTranslation = (
  dataTranslate: any,
  forceLanguage?: string | null,
) => {
  const theme = useContext(BrandSystemContext);
  const language: any = forceLanguage || theme?.language;

  if (typeof dataTranslate === 'string') {
    return dataTranslate;
  }
  const textTranslated = dataTranslate?.[language];

  if (textTranslated) {
    return textTranslated;
  }
  if (theme?.env === 'development') {
    console.warn({
      message: 'The translation not find with this parameter',
      dataTranslate,
      language: language,
    });
  }

  return '';
};
export const lockScroll = (
  lock: boolean,
  queryElt: keyof HTMLElementTagNameMap = 'body',
) => {
  const elt: any = document.querySelector(queryElt);

  if (lock) {
    elt.classList.add('overflow-hidden');
  } else {
    elt.classList.remove('overflow-hidden');
  }
};

export const debounce = <F extends (...params: any[]) => void>(
  fn: F,
  delay: number,
) => {
  let timeoutID: number | null = null;

  return function (this: any, ...args: any[]) {
    if (timeoutID) clearTimeout(timeoutID);
    timeoutID = window.setTimeout(() => fn.apply(this, args), delay);
  } as F;
};

export const isLink = (data: any) => {
  return 'href' in data || 'to' in data || data?.isButton;
};
